import Box from "@mui/material/Box";
import Collapse from "@mui/material/Collapse";
import IconButton from "@mui/material/IconButton";
import TableCell from "@mui/material/TableCell";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import { memo, useState } from "react";
import { Dropdown, DropdownButton } from "react-bootstrap";
import Select from "react-select";
import { toast } from "react-toastify";
import Expand from "./Expand";
import { IRows } from ".";
import { validateFields } from "Pages/LIS/Toxicology/CommonFunctions";
import { AddTemplateSettings } from "Services/PGXLis/ResultFilePreConfiguration";
import PermissionComponent, {
  getPermissionDisplayName,
} from "Shared/Common/Permissions/PermissionComponent";
import useLang from "Shared/hooks/useLanguage";
import {
  AddIcon,
  CrossIcon,
  DoneIcon,
  LoaderIcon,
  RemoveICon,
} from "Shared/Icons";
import { reactSelectSMStyle, styles } from "Utils/Common";

export interface ITableObj {
  templateId: number;
  labId: number;
  templateName: string;
  rowStatus: boolean;
}

const Row = (props: {
  row: any;
  rows: any;
  setRows: any;
  index: number;
  loadGridData: any;
  dropDownValues: any;
  setButtonClicked: any;
  queryDisplayTagNames: { [key: string]: string };
}) => {
  const {
    row,
    rows,
    index,
    setRows,
    loadGridData,
    dropDownValues,
    setButtonClicked,
    queryDisplayTagNames,
  } = props;

  const { t } = useLang();

  const [open, setOpen] = useState(false);
  const [request, setRequest] = useState(false);
  const [originalRowState, setOriginalRowState] = useState<any>(null);

  const getValues = (r: any, action: string) => {
    if (action === "Edit") {
      // Store original state before editing
      setOriginalRowState({ ...r });

      const updatedRows = rows.map((row: any) => {
        if (row.templateId === r.templateId) {
          return { ...row, rowStatus: true };
        }
        return row;
      });
      setRows(updatedRows);
    }
  };

  const handleChange = (name: string, value: string, templateId: number) => {
    setRows((curr: IRows[]) =>
      curr.map((x: IRows) =>
        x.templateId === templateId ? { ...x, [name]: value } : x
      )
    );
  };

  const handleIsActive = (event: any, templateId: any) => {
    setRows((curr: any) =>
      curr.map((x: any) =>
        x.templateId === templateId
          ? {
              ...x,
              isActive: event.target.checked,
            }
          : x
      )
    );
  };

  const handleSubmit = async (row: IRows) => {
    const queryModel = {
      templateId: row.templateId,
      labId: row.labId,
      reqTypeId: 11,
      templateName: row.templateName,
      isActive: row.isActive,
      translatorId: row.translatorId,
    };
    const { isValid, invalidField } = validateFields({
      templateName: row.templateName,
      labId: row.labId,
      translatorId: row.translatorId,
    });
    if (isValid) {
      try {
        setRequest(true);
        const res = await AddTemplateSettings(queryModel);

        if (res?.data.statusCode === 200) {
          toast.success(t(res?.data?.message));
          loadGridData(true, true);
        }
      } catch (err) {
        console.trace(err);
      } finally {
        setRequest(false);
        setButtonClicked(false);
      }
    } else {
      const errorMessage = invalidField
        ? `Please Fill ${queryDisplayTagNames[invalidField] ?? ""} field`
        : "Invalid field found";
      toast.error(errorMessage);
    }
  };

  return (
    <>
      <TableRow sx={{ "& > *": { borderBottom: "unset" } }}>
        <TableCell className="text-center">
          {!row.rowStatus ? (
            <IconButton
              aria-label="expand row"
              size="small"
              onClick={() => setOpen(!open)}
              className="btn btn-icon btn-icon-light btn-sm fw-bold btn-table-expend-row rounded h-20px w-20px min-h-20px"
            >
              {open ? (
                <button
                  id={`PGXPreconfTemplateSettingExpandClose_${row.templateId}`}
                  className="btn btn-icon btn-icon-light btn-sm fw-bold btn-table-expend-row rounded h-20px w-20px min-h-20px"
                >
                  <RemoveICon />
                </button>
              ) : (
                <button
                  id={`PGXPreconfTemplateSettingExpandOpen_${row.templateId}`}
                  className="btn btn-icon btn-icon-light btn-sm fw-bold btn-primary rounded h-20px w-20px min-h-20px"
                >
                  <AddIcon />
                </button>
              )}
            </IconButton>
          ) : null}
        </TableCell>
        <TableCell>
          <div className="d-flex justify-content-center">
            {row.rowStatus ? (
              <>
                <div className="gap-2 d-flex">
                  {request ? (
                    <button
                      id={`PGXPreconfTemplateSettingLoadButton`}
                      className="btn btn-icon btn-sm fw-bold btn-table-save btn-icon-light h-32px w-32px fas-icon-20px"
                    >
                      <LoaderIcon />
                    </button>
                  ) : (
                    <button
                      id={`PGXPreconfTemplateSettingSaveRow`}
                      onClick={() => {
                        handleSubmit(row);
                        setOriginalRowState(null); // Clear original state after save
                      }}
                      className="btn btn-icon btn-sm fw-bold btn-table-save btn-icon-light h-32px w-32px fas-icon-20px"
                    >
                      <DoneIcon />
                    </button>
                  )}
                  <button
                    id={`PGXPreconfTemplateSettingCancel`}
                    onClick={() => {
                      if (row.labId !== 0 && row.templateId !== 0) {
                        const updatedRows = rows.map((r: any) => {
                          if (r.templateId === row.templateId) {
                            return originalRowState
                              ? { ...originalRowState, rowStatus: false }
                              : { ...r, rowStatus: false };
                          }
                          return r;
                        });
                        loadGridData(true, false);
                        setRows(updatedRows);
                        setOriginalRowState(null);
                      } else {
                        const newArray = [...rows];
                        newArray.splice(index, 1);
                        setRows(newArray);
                        setRequest(false);
                        setButtonClicked(false);
                      }
                    }}
                    className="btn btn-icon btn-sm fw-bold btn-table-cancel btn-icon-light h-32px w-32px fas-icon-20px"
                  >
                    <CrossIcon />
                  </button>
                </div>
              </>
            ) : (
              <div className="rotatebtnn">
                <DropdownButton
                  className="p-0 del-before btn btn-light-info btn-active-info btn-sm btn-action table-action-btn"
                  key="end"
                  id={`PGXPreconfTemplateSetting3Dots_${row.templateId}`}
                  drop="end"
                  title={<i className="bi bi-three-dots-vertical p-0"></i>}
                >
                  <PermissionComponent
                    moduleName="PGX LIS"
                    pageName="Result File Pre-configuration"
                    permissionIdentifier="Edit"
                  >
                    {({ pageName, moduleName, permissionIdentifier }) => (
                      <Dropdown.Item
                        id={`PGXPreconfTemplateSettingEdit_${row.templateId}`}
                        className="w-auto"
                        eventKey="2"
                        onClick={() => getValues(row, "Edit")}
                      >
                        <i className={"fa fa-edit text-primary mr-2"}></i>
                        {getPermissionDisplayName(
                          pageName,
                          moduleName,
                          permissionIdentifier,
                          "Edit "
                        )}
                      </Dropdown.Item>
                    )}
                  </PermissionComponent>
                </DropdownButton>
              </div>
            )}
          </div>
        </TableCell>
        <TableCell
          id={`PGXPreconfTemplateSettingTemplateNameCell_${row.templateId}`}
          scope="row"
        >
          {row.rowStatus ? (
            <Select
              inputId={`PGXPreconfTemplateSettingTemplateSelect`}
              menuPortalTarget={document.body}
              styles={reactSelectSMStyle}
              placeholder={t("Select a Template")}
              theme={(theme: any) => styles(theme)}
              options={dropDownValues?.templateLookup}
              className={`${row?.templateId ? "bg-gray" : "bg-white"}`}
              name="templateName"
              onChange={(event: any) =>
                handleChange("templateName", event.label, row?.templateId)
              }
              value={dropDownValues?.templateLookup?.filter(function (
                option: any
              ) {
                return option.label === row.templateName;
              })}
              isDisabled={row?.templateId ? true : false}
            />
          ) : (
            <span className="px-2">{row?.templateName}</span>
          )}
        </TableCell>
        <TableCell
          id={`PGXPreconfTemplateSettingLabCell_${row.templateId}`}
          scope="row"
        >
          {row.rowStatus ? (
            <div className="p-2">
              <Select
                inputId={`PGXPreconfTemplateSettingLabSelect`}
                menuPortalTarget={document.body}
                styles={reactSelectSMStyle}
                placeholder={t("Select a Lab")}
                theme={(theme: any) => styles(theme)}
                options={dropDownValues?.referenceLab}
                className={`${row?.templateId ? "bg-gray" : "bg-white"}`}
                name="labId"
                onChange={(event: any) =>
                  handleChange("labId", event.value, row?.templateId)
                }
                value={dropDownValues?.referenceLab.filter(function (
                  option: any
                ) {
                  return option.value === row.labId;
                })}
                isDisabled={row?.templateId ? true : false}
              />
            </div>
          ) : (
            <span className="px-2">{row?.labName}</span>
          )}
        </TableCell>
        <TableCell
          id={`PGXPreconfTemplateSettingLabCell_${row.templateId}`}
          scope="row"
        >
          {row.rowStatus ? (
            <div className="p-2">
              <Select
                inputId={`PGXPreconfTemplateSettingTranslatorSelect`}
                menuPortalTarget={document.body}
                styles={reactSelectSMStyle}
                placeholder={t("Select a Translator")}
                theme={(theme: any) => styles(theme)}
                options={dropDownValues?.translatorLookup}
                className={`${row?.templateId ? "bg-gray" : "bg-white"}`}
                name="translatorId"
                onChange={(event: any) =>
                  handleChange("translatorId", event.value, row?.templateId)
                }
                value={dropDownValues?.translatorLookup?.filter(function (
                  option: any
                ) {
                  return option.value === row.translatorId;
                })}
              />
            </div>
          ) : (
            <span className="px-2">{row?.translatorName}</span>
          )}
        </TableCell>
        {row?.rowStatus ? (
          <TableCell>
            <div className="d-flex justify-content-center">
              <div className="form-check form-switch m-0">
                <input
                  id={`PGXPreconfTemplateSettingSwitchButton`}
                  className="form-check-input"
                  type="checkbox"
                  role="switch"
                  name="isActive"
                  checked={row?.isActive}
                  onChange={(event: any) =>
                    handleIsActive(event, row.templateId)
                  }
                />
              </div>
            </div>
          </TableCell>
        ) : (
          <TableCell
            id={`PGXPreconfTemplateSettingSwitchCell_${row.templateId}`}
          >
            <div className="d-flex justify-content-center">
              <div className="form-check form-switch">
                <input
                  className="form-check-input"
                  type="checkbox"
                  role="switch"
                  name="isActive"
                  checked={row?.isActive}
                  disabled={true}
                />
              </div>
            </div>
          </TableCell>
        )}
      </TableRow>
      <TableRow>
        <TableCell colSpan={7} className="padding-0">
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ margin: 1 }}>
              <Typography gutterBottom component="div">
                <div className="table-expend-sticky">
                  <div className="row">
                    <div className="col-lg-12 col-md-12 col-sm-12 col-xxl-12 px-lg-14 px-md-10 px-4">
                      <Expand
                        innerRow={row?.cells || []}
                        setOpen={setOpen}
                        loadGridData={loadGridData}
                      />
                    </div>
                  </div>
                </div>
              </Typography>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

export default memo(Row);
